import { useEffect, useMemo } from 'react';

import { usePageViews } from '@confluence/confluence-analytics/entry-points/usePageViews.ts';
import { usePageViewsAccess } from '@confluence/confluence-analytics/entry-points/usePageViewsAccess.ts';
import type { KnownAccount } from '@confluence/confluence-analytics/entry-points/accountTypes.ts';
import { expVal } from '@confluence/feature-experiments';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { useAgentViewersHealth } from './useAgentReadHealth.ts';

export type PageAgentViewer = KnownAccount & { isAgent: true };

export type UsePageAgentViewersResult = {
	agents: PageAgentViewer[];
	loading: boolean;
	error: Error | undefined;
};

/** Fetches while the preview panel is mounted; the modal receives the same result as props. */
export const usePageAgentViewers = (contentId: string): UsePageAgentViewersResult => {
	const skipAccess = !contentId || !expVal('ai_agent_analytics', 'isEnabled', false);
	const access = usePageViewsAccess(contentId, skipAccess);
	const skip = !access.canView;
	const {
		contentId: viewersContentId,
		viewers,
		loadViewers,
		isLoading,
		isLoadViewersCalled,
		error,
	} = usePageViews(contentId, {
		// The shared hook requests one extra row; keep the request within the 200-row API limit.
		limit: 199,
		skip,
		isPrivacyModeEnabled: access.isPrivacyModeEnabled,
		includeViewersActorType: true,
		includeAgentTypePreview: false,
	});

	useEffect(() => {
		if (!skip) {
			loadViewers();
		}
	}, [skip, loadViewers]);

	const agents = useMemo(
		() =>
			skip || viewersContentId !== contentId
				? []
				: (viewers ?? []).filter(
						(viewer): viewer is PageAgentViewer => 'isAgent' in viewer && viewer.isAgent === true,
					),
		[skip, viewersContentId, contentId, viewers],
	);
	const loading = access.loading || (!skip && (isLoading || !isLoadViewersCalled));
	const resultError = access.error ?? (skip ? undefined : error);
	useAgentViewersHealth({
		contentId,
		accessEnabled: !skipAccess,
		access,
		loading: isLoading || !isLoadViewersCalled,
		current: viewersContentId === contentId,
		viewers: agents,
		error,
	});
	useEffect(() => {
		if (resultError && !isErrorMarkedAsHandled(resultError)) {
			getMonitoringClient().submitError(resultError, { attribution: Attribution.INTEGRATIONS });
			markErrorAsHandled(resultError);
		}
	}, [resultError]);

	return useMemo(() => ({ agents, loading, error: resultError }), [agents, loading, resultError]);
};
